import { NgModule } from '@angular/core';
import { RouterModule, Routes } from '@angular/router';
import { BookmeBookComponent } from './bookme-book/bookme-book.component';
import { BookmeCancelComponent } from './bookme-cancel/bookme-cancel.component';
import { BookmeConfirmationComponent } from './bookme-confirmation/bookme-confirmation.component';
import { BookmeContactFormComponent } from './bookme-contact-form/bookme-contact-form.component';
import { BookmeLandingPageComponent } from './bookme-landing-page/bookme-landing-page.component';
import { BookmeMeetingTypeSelectionPageComponent } from './bookme-meeting-type-selection-page/bookme-meeting-type-selection-page.component';
import { BookmeRescheduleComponent } from './bookme-reschedule/bookme-reschedule.component';
import { BookmeRescheduleConfirmationComponent } from './bookme-reschedule/confirmation/confirmation.component';
import { RescheduleConfirmChangesComponent } from './bookme-reschedule/reschedule-confirm-changes/reschedule-confirm-changes.component';
import { RescheduleSuccessComponent } from './bookme-reschedule/reschedule-success/reschedule-success.component';
import { BookmeComponent } from './bookme/bookme.component';
import { CancelConfirmationPageComponent } from './cancel-confirmation/cancel-confirmation.component';
import { CancelFeatureFlagGuard } from './cancel-feature-flag.guard';
import { CancelInfoPageComponent } from './cancel-info/cancel-info.component';
import { GroupSelectionPageComponent } from './group-selection-page/group-selection-page.component';
import { LegacyRedirectGuard } from './legacy-redirect-guard.service';
import { MeetingInfoPageComponent } from './meeting-info/meeting-info.component';
import { ServiceSelectionPageComponent } from './service-selection-page/service-selection-page.component';
import { ServiceTabFeatureFlagGuard } from './service-tab-feature-flag.guard';
import { BookingLayoutComponent } from './booking-layout/booking-layout.component';
import { SkipToContactFormGuard } from './skip-to-contact-form.guard';

const routes: Routes = [
  {
    path: 'service/:serviceId/:serviceSlug',
    component: BookingLayoutComponent,
    canMatch: [ServiceTabFeatureFlagGuard],
  },
  {
    path: 'group/:groupId/:groupSlug',
    component: BookingLayoutComponent,
    canMatch: [ServiceTabFeatureFlagGuard],
  },
  {
    path: 'book/:calendarId/:meetingTypeId',
    component: BookingLayoutComponent,
    canMatch: [ServiceTabFeatureFlagGuard],
  },
  {
    path: 'book/:calendarId/:meetingTypeId',
    component: BookmeComponent,
    children: [
      {
        path: '',
        component: BookmeBookComponent,
        data: { animationState: 'calendar' },
        canActivate: [SkipToContactFormGuard],
      },
      {
        path: 'contact-form',
        component: BookmeContactFormComponent,
        data: { animationState: 'form' },
      },
      {
        path: 'confirmation',
        component: BookmeConfirmationComponent,
        data: { animationState: 'confirmation' },
      },
    ],
  },
  {
    path: 'book/:calendarId',
    redirectTo: ':calendarId',
  },
  {
    path: 'cancels/:calendarId/:meetingId',
    component: BookmeComponent,
    canActivate: [CancelFeatureFlagGuard],
    children: [
      { path: '', component: BookmeCancelComponent },
      { path: 'confirmation', component: BookmeConfirmationComponent },
    ],
  },
  {
    path: 'reschedules/:calendarId/:meetingId',
    component: BookingLayoutComponent,
    canMatch: [ServiceTabFeatureFlagGuard],
    data: { mode: 'reschedule' },
  },
  {
    path: 'reschedules/:calendarId/:meetingId/confirm-changes',
    component: BookmeComponent,
    canMatch: [ServiceTabFeatureFlagGuard],
    children: [{ path: '', component: RescheduleConfirmChangesComponent }],
  },
  {
    path: 'reschedules/:calendarId/:meetingId',
    component: BookmeComponent,
    children: [
      { path: '', component: BookmeRescheduleComponent },
      {
        path: 'reschedule-confirmation', // old page. Needs to be removed once we remove feaature flag of sms in bookme client
        component: BookmeRescheduleConfirmationComponent,
      },
      { path: 'confirmation', component: BookmeConfirmationComponent }, // old page. Needs to be removed
      { path: 'confirm-changes', component: RescheduleConfirmChangesComponent },
    ],
  },
  {
    path: 'meeting-info/:meetingId',
    component: BookmeComponent,
    children: [{ path: '', component: MeetingInfoPageComponent }],
  },
  {
    path: 'cancel-info/:meetingId',
    component: BookmeComponent,
    children: [
      { path: '', component: CancelInfoPageComponent },
      { path: 'cancelled', component: CancelConfirmationPageComponent },
    ],
  },
  {
    path: 'reschedule-info/:meetingId',
    component: BookmeComponent,
    children: [{ path: '', component: RescheduleSuccessComponent }],
  },
  // Session routes - no host required, direct loading
  {
    path: 'session',
    loadChildren: () =>
      import('./session/session.module').then((m) => m.SessionModule),
  },
  {
    path: 'group/:groupId/:groupSlug',
    component: BookmeComponent,
    children: [{ path: '', component: GroupSelectionPageComponent }],
  },
  {
    path: 'service/:serviceId/:serviceSlug',
    component: BookmeComponent,
    children: [{ path: '', component: ServiceSelectionPageComponent }],
  },
  // Public meeting sharing routes - must be before :calendarId catch-all
  {
    path: 'meeting/:namespaceID/:publicId',
    component: BookmeComponent,
    children: [
      {
        path: '',
        loadComponent: () =>
          import('./shared-meeting-page/shared-meeting-page.component').then(
            (m) => m.SharedMeetingPageComponent,
          ),
      },
    ],
  },
  // Old public meeting sharing routes
  {
    path: 'video/:namespaceID/:publicId',
    component: BookmeComponent,
    children: [
      {
        path: '',
        loadComponent: () =>
          import('./shared-meeting-page/shared-meeting-page.component').then(
            (m) => m.SharedMeetingPageComponent,
          ),
      },
    ],
  },
  {
    path: ':calendarId',
    component: BookingLayoutComponent,
    canMatch: [ServiceTabFeatureFlagGuard],
  },
  {
    path: ':calendarId',
    component: BookmeComponent,
    children: [
      { path: '', component: BookmeMeetingTypeSelectionPageComponent },
    ],
  },

  {
    path: '',
    pathMatch: 'full',
    component: BookmeLandingPageComponent,
    canActivate: [LegacyRedirectGuard],
    canActivateChild: [LegacyRedirectGuard],
  },

  // TODO: The below routes will redirect traffic from legacy routes.
  {
    path: 'reschedules/:meetingId',
    component: BookmeComponent,
    canActivate: [LegacyRedirectGuard],
    canActivateChild: [LegacyRedirectGuard],
  },
  {
    path: 'cancels/:meetingId',
    component: BookmeComponent,
    canActivate: [CancelFeatureFlagGuard, LegacyRedirectGuard],
    canActivateChild: [LegacyRedirectGuard],
  },
  {
    path: 'terms',
    loadChildren: () =>
      import('./terms/terms.module').then((m) => m.TermsModule),
  },
  // TODO: Not sure if these redirects are required by Google Auth in Meeting Scheduler.
  {
    path: 'privacy-policy',
    redirectTo: 'terms/privacy-policy',
    pathMatch: 'full',
  },
  {
    path: 'terms-of-service',
    redirectTo: 'terms/terms-of-service',
    pathMatch: 'full',
  },
  {
    path: 'privacy-policy/third-party-subprocessors',
    redirectTo: 'terms/third-party-subprocessors',
    pathMatch: 'full',
  },
  {
    path: 'privacy-policy/data-processing-addendum',
    redirectTo: 'terms/data-processing-addendum',
    pathMatch: 'full',
  },
  {
    path: '**',
    redirectTo: '',
  },
];

@NgModule({
  imports: [
    RouterModule.forRoot(routes, {
      initialNavigation: 'enabledBlocking',
      paramsInheritanceStrategy: 'always',
    }),
  ],
  exports: [RouterModule],
})
export class AppRoutingModule {}
